/**
 * Client-side replacement for the /document/check_positioning polling call.
 * Orchestrates: corner detection (documentAligner.js) -> perspective warp ->
 * blur check -> glare check (documentGlare.js), on the live camera frame.
 *
 * Returns the same shape checkDocumentDetection already consumed from the
 * server (`{ boxes: { xyxyn } | null, has_glare, is_blurry }`), plus the four
 * detected `corners` (clockwise, normalized like xyxyn) for callers that need
 * the document's actual outline rather than its bounding box.
 */
import { runAligner } from "./documentAligner";
import { detectGlare } from "./documentGlare";
import { loadOpenCv, MatTracker } from "./openCvLoader";

const WARP_WIDTH = 800;
const WARP_HEIGHT = 480;
export const BLUR_THRESHOLD = 65.0; // matches ImageProcessingConfig.blur_threshold default
// Max width of the card crop pulled from the native video frame for the blur
// check. Kept at 1.5x WARP_WIDTH so the warp to 800x480 is a mild downscale
// (preserves real sharpness) rather than the ~2x upscale the preview-size
// frame forced, which smoothed the card and dragged every score down.
const BLUR_CROP_MAX_WIDTH = 1200;

export { loadOpenCv };

/** Mirrors capybara.structures.polygons.order_points_clockwise. */
export function orderCornersClockwise(corners) {
  const byX = [...corners].sort((a, b) => a.x - b.x);
  const [p0, p1] = byX.slice(0, 2).sort((a, b) => a.y - b.y);
  const [p2, p3] = byX.slice(2, 4).sort((a, b) => a.y - b.y);
  // p0=tl, p1=bl, p2=tr, p3=br
  return [p0, p2, p3, p1]; // [tl, tr, br, bl]
}

/** Mirrors the min/max bbox math in check_positioning (validation.py). */
export function computeBoundingBoxXYXYN(corners, origWidth, origHeight) {
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  return {
    x1: Math.min(...xs) / origWidth,
    y1: Math.min(...ys) / origHeight,
    x2: Math.max(...xs) / origWidth,
    y2: Math.max(...ys) / origHeight,
  };
}

/** Mirrors capybara.vision.geometric.imwarp_quadrangle(dst_size=(800,480)). */
function warpDocument(cv, bgrMat, orderedCorners, tracker) {
  const srcTri = tracker.add(cv.matFromArray(4, 1, cv.CV_32FC2, [
    orderedCorners[0].x, orderedCorners[0].y,
    orderedCorners[1].x, orderedCorners[1].y,
    orderedCorners[2].x, orderedCorners[2].y,
    orderedCorners[3].x, orderedCorners[3].y,
  ]));
  const dstTri = tracker.add(cv.matFromArray(4, 1, cv.CV_32FC2, [
    0, 0,
    WARP_WIDTH, 0,
    WARP_WIDTH, WARP_HEIGHT,
    0, WARP_HEIGHT,
  ]));
  const matrix = tracker.add(cv.getPerspectiveTransform(srcTri, dstTri));
  const warped = tracker.add(new cv.Mat());
  cv.warpPerspective(
    bgrMat, warped, matrix, new cv.Size(WARP_WIDTH, WARP_HEIGHT),
    cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar()
  );
  return warped;
}

/** Laplacian variance relative to gray-level variance; floored at 1 so a flat image can't divide by zero. */
export function normalizeBlurScore(laplacianVariance, grayVariance) {
  return laplacianVariance / Math.max(grayVariance, 1);
}

/**
 * Mirrors ImageQualityService._detect_blur (Laplacian variance), and also
 * returns that variance divided by the card's gray-level variance. The raw
 * variance depends on what is printed on the card — a dense barcode back
 * scores far higher than a faded front at identical focus — so the
 * normalized score is the content-independent one to compare across sides.
 */
function detectBlur(cv, warpedBgrMat, tracker) {
  const gray = tracker.add(new cv.Mat());
  cv.cvtColor(warpedBgrMat, gray, cv.COLOR_BGR2GRAY);
  const lap = tracker.add(new cv.Mat());
  cv.Laplacian(gray, lap, cv.CV_64F);
  const mean = tracker.add(new cv.Mat());
  const stddev = tracker.add(new cv.Mat());
  cv.meanStdDev(lap, mean, stddev);
  const variance = stddev.data64F[0] ** 2;

  const grayMean = tracker.add(new cv.Mat());
  const grayStddev = tracker.add(new cv.Mat());
  cv.meanStdDev(gray, grayMean, grayStddev);
  const grayVariance = grayStddev.data64F[0] ** 2;

  return {
    isBlurry: variance < BLUR_THRESHOLD,
    blurScore: variance,
    blurScoreNormalized: normalizeBlurScore(variance, grayVariance),
  };
}

/**
 * Pulls just the card's bounding box out of the native-resolution video frame
 * (the aligner corners are normalized, and the preview canvas has the video's
 * aspect ratio, so they map straight onto native pixels) and returns it with
 * the corners shifted into the crop's coordinate space.
 */
export function cropCardFromVideo(video, orderedCorners, canvas) {
  const scaleX = video.videoWidth / canvas.width;
  const scaleY = video.videoHeight / canvas.height;
  const native = orderedCorners.map((p) => ({ x: p.x * scaleX, y: p.y * scaleY }));
  const x0 = Math.max(0, Math.floor(Math.min(...native.map((p) => p.x))));
  const y0 = Math.max(0, Math.floor(Math.min(...native.map((p) => p.y))));
  const x1 = Math.min(video.videoWidth, Math.ceil(Math.max(...native.map((p) => p.x))));
  const y1 = Math.min(video.videoHeight, Math.ceil(Math.max(...native.map((p) => p.y))));
  const cropWidth = x1 - x0;
  const cropHeight = y1 - y0;
  const shrink = Math.min(1, BLUR_CROP_MAX_WIDTH / cropWidth);

  const crop = document.createElement("canvas");
  crop.width = Math.round(cropWidth * shrink);
  crop.height = Math.round(cropHeight * shrink);
  crop.getContext("2d").drawImage(video, x0, y0, cropWidth, cropHeight, 0, 0, crop.width, crop.height);
  return {
    canvas: crop,
    corners: native.map((p) => ({ x: (p.x - x0) * shrink, y: (p.y - y0) * shrink })),
  };
}

/**
 * Runs the full client-side positioning/blur/glare check on a canvas
 * snapshot of the current camera frame (preview size, BGR-agnostic —
 * conversion happens internally) for detection and glare, and on a crop of the
 * `video` element's native-resolution frame for blur. Returns the same
 * response shape the old /document/check_positioning endpoint did.
 */
export async function runQualityCheck(canvas, video) {
  const [alignerResult, cv] = await Promise.all([runAligner(canvas), loadOpenCv()]);
  const { corners, hasObject } = alignerResult;

  if (!hasObject) {
    return { boxes: null, corners: null, has_glare: false, is_blurry: false };
  }

  const xyxyn = computeBoundingBoxXYXYN(corners, canvas.width, canvas.height);
  const ordered = orderCornersClockwise(corners);

  const tracker = new MatTracker();
  try {
    const rgba = tracker.add(cv.imread(canvas));
    const bgr = tracker.add(new cv.Mat());
    cv.cvtColor(rgba, bgr, cv.COLOR_RGBA2BGR);

    const warped = warpDocument(cv, bgr, ordered, tracker);
    const { hasGlare } = detectGlare(cv, warped, tracker);

    const hiRes = cropCardFromVideo(video, ordered, canvas);
    const hiResRgba = tracker.add(cv.imread(hiRes.canvas));
    const hiResBgr = tracker.add(new cv.Mat());
    cv.cvtColor(hiResRgba, hiResBgr, cv.COLOR_RGBA2BGR);
    const hiResWarped = warpDocument(cv, hiResBgr, hiRes.corners, tracker);
    const { isBlurry, blurScore, blurScoreNormalized } = detectBlur(cv, hiResWarped, tracker);

    return {
      boxes: { xyxyn },
      corners: ordered.map((p) => ({ x: p.x / canvas.width, y: p.y / canvas.height })),
      has_glare: hasGlare,
      is_blurry: isBlurry,
      blur_score: blurScore,
      blur_score_normalized: blurScoreNormalized,
    };
  } finally {
    tracker.deleteAll();
  }
}
